import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";
import { db } from "initializer";
import { TurboRoom } from "./records";
import { actions } from "./slice";
import { DefaultThunk } from "stores";
import { getCDNConfig } from "../app.state/operations";

export const loadTurboRoom =
  (roomId: string): DefaultThunk<Promise<TurboRoom | null>> =>
  async () => {
    try {
      const docRef = doc(db, "turbo_rooms", roomId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data() as TurboRoom;
      }
      return null;
    } catch (error) {
      console.error("Error loading turbo room:", error);
      return null;
    }
  };

export const updateTurboRoom =
  (
    roomId: string,
    enabled: boolean,
    cdnUrl?: string
  ): DefaultThunk<Promise<void>> =>
  async () => {
    try {
      const docRef = doc(db, "turbo_rooms", roomId);

      let effectiveCdnUrl = cdnUrl || "";

      // If enabled and no specific cdnUrl provided, randomly select from available servers
      if (enabled && !cdnUrl) {
        const cdnConfig = await getCDNConfig();
        if (cdnConfig?.servers) {
          const enabledServers = cdnConfig.servers.filter(
            (server) => server.enabled
          );
          if (enabledServers.length > 0) {
            const randomIndex = Math.floor(
              Math.random() * enabledServers.length
            );
            effectiveCdnUrl = enabledServers[randomIndex].url;
          }
        }
      }

      const turboRoomData: Partial<TurboRoom> = {
        roomId,
        enabled,
        cdnUrl: effectiveCdnUrl,
        timestamp: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        turboRoomData.createdAt = serverTimestamp();
      }

      await setDoc(docRef, turboRoomData, { merge: true });
    } catch (error) {
      console.error("Error updating turbo room:", error);
      throw error;
    }
  };

export const autoUpdateTurboRoom =
  (roomId: string): DefaultThunk<Promise<void>> =>
  async (dispatch) => {
    try {
      const docRef = doc(db, "turbo_rooms", roomId);
      const docSnap = await getDoc(docRef);

      // If turbo room document exists
      if (docSnap.exists()) {
        const currentData = docSnap.data() as TurboRoom;

        // Check if it was updated within the last 24 hours
        const now = Date.now();
        const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;

        let lastUpdate: number | null = null;
        if (currentData.updatedAt) {
          // Handle Firestore Timestamp
          if ((currentData.updatedAt as any).toMillis) {
            lastUpdate = (currentData.updatedAt as any).toMillis();
          } else if ((currentData.updatedAt as any)._seconds) {
            lastUpdate = (currentData.updatedAt as any)._seconds * 1000;
          }
        }

        // If turbo mode is enabled and within 24 hours, just refresh
        if (
          currentData.enabled &&
          lastUpdate &&
          lastUpdate > twentyFourHoursAgo
        ) {
          // Refresh to ensure CDN URL is still valid
          await dispatch(refreshTurboRoom(roomId));
          return;
        }

        // If user explicitly disabled within 24 hours, respect their choice
        if (
          !currentData.enabled &&
          lastUpdate &&
          lastUpdate > twentyFourHoursAgo
        ) {
          return;
        }
      }

      // If no recent user preference or document doesn't exist, enable turbo mode
      const cdnConfig = await getCDNConfig();
      let cdnUrl = "";

      if (cdnConfig?.servers) {
        const enabledServers = cdnConfig.servers.filter(
          (server) => server.enabled
        );
        if (enabledServers.length > 0) {
          const randomIndex = Math.floor(Math.random() * enabledServers.length);
          cdnUrl = enabledServers[randomIndex].url;
        }
      }

      const turboRoomData: Partial<TurboRoom> = {
        roomId,
        enabled: true,
        cdnUrl,
        timestamp: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (!docSnap.exists()) {
        turboRoomData.createdAt = serverTimestamp();
      }

      await setDoc(docRef, turboRoomData, { merge: true });
      console.log("Auto-enabled turbo mode for Pro owner");
    } catch (error) {
      console.error("Error auto-updating turbo room:", error);
      // Don't throw - this is a non-critical feature
    }
  };

export const refreshTurboRoom =
  (roomId: string): DefaultThunk<Promise<void>> =>
  async () => {
    try {
      const docRef = doc(db, "turbo_rooms", roomId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        throw new Error("Turbo room not found");
      }

      const currentData = docSnap.data() as TurboRoom;
      let cdnUrl = currentData.cdnUrl;

      // Check if the current CDN URL is still valid
      if (currentData.enabled && cdnUrl) {
        const cdnConfig = await getCDNConfig();
        if (cdnConfig?.servers) {
          const serverExists = cdnConfig.servers.some(
            (server) => server.enabled && server.url === cdnUrl
          );

          // If server doesn't exist or is disabled, select a new one
          if (!serverExists) {
            const enabledServers = cdnConfig.servers.filter(
              (server) => server.enabled
            );
            if (enabledServers.length > 0) {
              const randomIndex = Math.floor(
                Math.random() * enabledServers.length
              );
              cdnUrl = enabledServers[randomIndex].url;
            } else {
              // No enabled servers available - don't update
              return;
            }
          }
        }
      }

      await setDoc(docRef, {
        ...currentData,
        cdnUrl,
        timestamp: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error refreshing turbo room:", error);
      throw error;
    }
  };

export const subscribeTurboRoom =
  (roomId: string): DefaultThunk<() => void> =>
  (dispatch, getState) => {
    const docRef = doc(db, "turbo_rooms", roomId);

    const unsubscribe = onSnapshot(
      docRef,
      { includeMetadataChanges: false }, // Only listen to actual data changes, not pending writes
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const currentTurboRoom = getState().entities.turboRooms?.[roomId];

          // If timestamp is null (pending serverTimestamp), use the previous timestamp
          const turboRoom: TurboRoom = {
            ...data,
            timestamp: data.timestamp || currentTurboRoom?.timestamp || null,
            createdAt: data.createdAt || currentTurboRoom?.createdAt || null,
            updatedAt: data.updatedAt || currentTurboRoom?.updatedAt || null,
          } as TurboRoom;

          dispatch(actions.set({ roomId, turboRoom }));
        } else {
          // Set empty turbo room instead of removing
          const emptyTurboRoom: TurboRoom = {
            roomId,
            enabled: false,
            cdnUrl: "",
            timestamp: null,
            createdAt: null,
            updatedAt: null,
          };
          dispatch(actions.set({ roomId, turboRoom: emptyTurboRoom }));
        }
      },
      (error) => {
        console.error("Error in turbo room subscription:", error);
        // Set empty turbo room on error
        const emptyTurboRoom: TurboRoom = {
          roomId,
          enabled: false,
          cdnUrl: "",
          timestamp: null,
          createdAt: null,
          updatedAt: null,
        };
        dispatch(actions.set({ roomId, turboRoom: emptyTurboRoom }));
      }
    );

    return unsubscribe;
  };
